/**
 * Filter for errors thrown by browser extensions.
 * Lives here rather than in `@ringba/core/telemetry` because it reads the Datadog SDK's stack format.
 */

import type { EventFilter, TelemetryEvent } from '@ringba/core/telemetry';

const EXTENSION_URL_PREFIXES = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://'];

// The SDK's frame format is `  at <func> @ <url>:<line>:<col>`.
// Only the top frame decides: an extension that wraps the app's callbacks throws from its own frame, with the app's frames below it.
const TOP_FRAME_URL = /^ {2}at .*? @ (\S+)/m;

/**
 * Drops an error event whose top stack frame is a browser extension's script.
 * An event with no stack, or a stack with no frame line, is kept.
 */
export function dropExtensionErrors<T extends TelemetryEvent>(): EventFilter<T> {
  return (event) => {
    if (event.type !== 'error') return true;

    const topFrameUrl = event.error?.stack?.match(TOP_FRAME_URL)?.[1];
    if (!topFrameUrl) return true;

    return !EXTENSION_URL_PREFIXES.some((prefix) => topFrameUrl.startsWith(prefix));
  };
}
